import * as vp from '../components/Utility/VoPayApi/VoPayApi.types';

import type { BalanceTotal, CurrentVsAverage, ToWidgetData } from '../components/DataDisplay/Widget/types';

export type DataTransformers = {
  fromAccounts: (accountsMap?: vp.AccountMap) => TransformedAccounts;
  fromObject: <T>(obj: Record<string, T>) => { toArray: () => Array<T> };
  fromPartnerStats: (stats: vp.PartnerStatistics) => TransformedPartnerStats;
};

export type TransformedAccounts = {
  toAccountDetails: vp.AccountDetailsMap;
  toBalances: vp.AccountBalance[];
  toPermissions: vp.AccountPermissionsMap;
  toStatusData: vp.AccountStatusData;
};

export type TransformedPartnerStats = {
  toWidgetData: ToWidgetData;
};

const dataTransformers: DataTransformers = {
  fromAccounts: (accountsMap = {}) => {
    const accountValues = Object.values(accountsMap);

    const initValues = {
      toAccountDetails: {},
      toBalances: [],
      toPermissions: {},
      toStatusData: { activeCount: 0, inactiveCount: 0, totalCount: 0 },
    } as TransformedAccounts;

    return accountValues.reduce(
      ({ toAccountDetails, toBalances, toPermissions, toStatusData }, currentAccount) => ({
        toAccountDetails: buildAccountDetails(toAccountDetails, currentAccount),
        toBalances: buildBalances(toBalances, currentAccount),
        toPermissions: buildPermissions(toPermissions, currentAccount),
        toStatusData: buildStatusData(toStatusData, currentAccount),
      }),
      initValues,
    );

    function buildAccountDetails(allAccountDetails: vp.AccountDetailsMap, currentAccount: vp.Account) {
      const {
        AccountID,
        AccountName,
        DBAName,
        Address,
        Address2,
        BillingPackageID,
        ClientAccountsEnabled,
        LegalBusinessName,
        City,
        Province,
        PostalCode,
        Country,
        Email,
        Fax,
        Phone,
        Token,
        ParentAccountName,
        PartnerAccountName,
        PartnerAccountID,
        // DateIncorporation,
        // FullLegalName,
        // NatureOfBusiness,
        // Occupation,
        // OrganizationalType,
        // OriginatorName,
        // OriginatorShortName,
        // RegistrationNumber,
        // RegistrationProvince,
      } = currentAccount;

      const accountDetails = {
        accountId: AccountID,
        accountName: AccountName,
        billingPackageId: BillingPackageID,
        clientAccountsEnabled: ClientAccountsEnabled,
        ParentAccountName: ParentAccountName,
        DBAName: DBAName || '',
        legalBusinessName: LegalBusinessName || '',
        PartnerAccountName: PartnerAccountName,
        PartnerAccountID: PartnerAccountID,
        contact: {
          email: Email,
          fax: Fax,
          phone: Phone,
        },
        address: {
          addressLine1: Address,
          addressLine2: Address2,
          city: City,
          province: Province,
          postalCode: PostalCode,
          country: Country,
        },
        token: Token,
      };

      return { ...allAccountDetails, [AccountID]: accountDetails };
    }

    function buildBalances(allBalances: vp.AccountBalance[], currentAccount: vp.Account): vp.AccountBalance[] {
      // An account the backend returns no balance for (one still being onboarded, typically) is
      // still an account: it gets one zero row in its country's currency rather than vanishing from
      // the list — which is what made a pending-status filter come back empty.
      const balances: vp.Balance[] =
        currentAccount.Balances.length > 0
          ? currentAccount.Balances
          : [
              {
                Currency: currentAccount.Country === 'United States' ? 'USD' : 'CAD',
                AccountBalance: '0.00',
                AvailableBalance: '0.00',
                AvailableImmediately: '0.00',
                PendingBalance: '0.00',
                Reserve: '0.00',
              },
            ];

      const accountBalances = balances.map((balance) => {
        const subAccountBalances = currentAccount.SubAccounts.flatMap((subAccount) => {
          // Filter sub-accounts to only include those matching the current balance's currency
          const matchingBalances = subAccount.Balances.filter((subBalance) => subBalance.Currency === balance.Currency);

          if (matchingBalances.length === 0) {
            return [];
          }

          return buildBalances([], subAccount);
        });

        const balancePlus: vp.AccountBalance = {
          ...currentAccount,
          ...balance,
          SubAccounts: subAccountBalances.filter((sub) => sub.Currency === balance.Currency),
        };

        return balancePlus;
      });

      return [...allBalances, ...accountBalances];
    }

    function buildPermissions(allPermissions: vp.AccountPermissionsMap, currentAccount: vp.Account) {
      const {
        AccountID,
        EFTCollectEnabled,
        EFTSendEnabled,
        USDEFTCollectEnabled,
        USDEFTSendEnabled,
        InteracMoneyRequestEnabled,
        InteracBulkPayoutEnabled,
        GCMEnabled,
        PushToCardEnabled,
        RTRWithdrawEnabled,
        PortalViewOnly,
        AutoBalanceTransferEnabled,
        PayLinkEnabled,
        AddUserEnabled,
        MaxFundEFTAmount,
        MaxGCMAmount,
        MaxWithdrawEFTAmount,
        MaxInteracMoneyAmount,
        MaxInteracBulkPayoutAmount,
        MaxRTRWithdrawAmount,
        MaxPushToCardAmount,
        DailyLimitAmount,
        WeeklyLimitAmount,
        MonthlyLimitAmount,
        MonthlyTransactionLimit,
        MaxACHFundAmount,
        MaxACHWithdrawAmount,
        SameDayMaxACHFundAmount,
        SameDayMaxACHWithdrawAmount,
        MaxWireAmount,
        ACHFundEnabled,
        ACHWithdrawEnabled,
        SameDayACHFundEnabled,
        SameDayACHWithdrawEnabled,
        WireEnabled,
        MaxFundMyAccountAmount,
        MaxWithdrawMyAccountAmount,
        CreditCardEnabled,
        IQ11Enabled,
        BrandingELinxEnabled,
        BrandingEmailEnabled,
        BrandingOnboardingEnabled,
        BrandingPortalEnabled,
        KYCLightEnabled,
      } = currentAccount;

      const accountPermissions: vp.AccountPermissions = {
        EFTCollectEnabled,
        EFTSendEnabled,
        USDEFTCollectEnabled,
        USDEFTSendEnabled,
        InteracMoneyRequestEnabled,
        InteracBulkPayoutEnabled,
        GCMEnabled,
        PushToCardEnabled,
        RTRWithdrawEnabled,
        AutoBalanceTransferEnabled,
        PayLinkEnabled,
        AddUserEnabled,
        MaxGCMAmount,
        MaxInteracMoneyAmount,
        MaxInteracBulkPayoutAmount,
        MaxRTRWithdrawAmount,
        MaxPushToCardAmount,
        MaxFundEFTAmount,
        MaxWithdrawEFTAmount,
        DailyLimitAmount,
        WeeklyLimitAmount,
        MonthlyLimitAmount,
        MonthlyTransactionLimit,
        MaxACHFundAmount,
        SameDayMaxACHFundAmount,
        SameDayMaxACHWithdrawAmount,
        MaxWireAmount,
        MaxACHWithdrawAmount,
        ACHFundEnabled,
        ACHWithdrawEnabled,
        SameDayACHFundEnabled,
        SameDayACHWithdrawEnabled,
        WireEnabled,
        PortalTransactionCreation: !PortalViewOnly,
        MaxFundMyAccountAmount,
        MaxWithdrawMyAccountAmount,
        CreditCardEnabled,
        IQ11Enabled,
        BrandingELinxEnabled: !!BrandingELinxEnabled,
        BrandingEmailEnabled: !!BrandingEmailEnabled,
        BrandingOnboardingEnabled: !!BrandingOnboardingEnabled,
        BrandingPortalEnabled: !!BrandingPortalEnabled,
        KYCLightEnabled,
      };

      return { ...allPermissions, [AccountID]: accountPermissions };
    }

    function buildStatusData(allStatusData: vp.AccountStatusData, currentAccount: vp.Account): vp.AccountStatusData {
      const { activeCount, inactiveCount, totalCount } = allStatusData;

      return {
        activeCount: currentAccount.IsActive ? activeCount + 1 : activeCount,
        inactiveCount: currentAccount.IsActive ? inactiveCount : inactiveCount + 1,
        totalCount: totalCount + 1,
      };
    }
  },
  fromObject: (obj = {}) => ({
    toArray: () => Object.values(obj),
  }),
  fromPartnerStats: ({ balances, statusCount, transactions }) => {
    const { totals } = balances;
    const { dailyAverages, dailyBreakdown, dailyCounts } = transactions;

    function formatAccountStatusData() {
      return [
        { label: 'Active', total: statusCount.total, active: statusCount.active },
        { label: 'Inactive', total: statusCount.total, inactive: statusCount.inactive },
      ];
    }

    function formatBalanceTotals() {
      const initData: { [currency: string]: BalanceTotal[] } = {};

      const typeMap: { [type: string]: string } = {
        AvailableBalance: 'Available Balance',
        AccountBalance: 'Account Balance',
        PendingBalance: 'Pending Balance',
        AvailableImmediately: 'Available Immediately',
        Reserve: 'Reserve Amount',
      };

      return Object.entries(totals).reduce((accumulator, [currency, typeCount]) => {
        accumulator[currency] = Object.entries(typeCount).map(([type, count]) => ({
          label: typeMap[type] || type,
          total: count,
        }));

        return accumulator;
      }, initData);
    }

    function formatDailyTransactionData() {
      const initData: { [date: string]: CurrentVsAverage[] } = {};

      return Object.entries(dailyBreakdown).reduce((accumulator, [date, typeCount]) => {
        accumulator[date] = Object.entries(typeCount).map(([type, count]) => ({
          label: type,
          current: count,
          average: dailyAverages[type],
        }));

        return accumulator;
      }, initData);
    }

    function formatWeeklyTransactionData() {
      const dailyCountEntries = Object.entries(dailyCounts);

      if (dailyCountEntries.length === 0) return [];

      const [startingDate] = dailyCountEntries[0];
      const numberOfDays = 7;

      const dateList = buildDateList(startingDate, numberOfDays);

      return dateList.map((date) => {
        const current = dailyCounts[date] || 0;
        const previous = dailyCounts[calculateEarlierDate(date, 7)] || 0;

        return { label: date, current, previous };
      });
    }

    function buildDateList(startingDate: string, length: number): string[] {
      return new Array(length).fill('').map((_, idx) => calculateEarlierDate(startingDate, idx));
    }

    function calculateEarlierDate(yyyymmdd: string, daysAgo: number): string {
      const date = new Date(yyyymmdd);

      date.setDate(date.getDate() - daysAgo);
      const earlierDate = date.toISOString().split('T')[0];

      return earlierDate;
    }

    return {
      toWidgetData: {
        accountStatus: formatAccountStatusData(),
        balanceTotals: formatBalanceTotals(),
        dailyTransactionBreakdown: formatDailyTransactionData(),
        weeklyTransactionVolume: formatWeeklyTransactionData(),
      },
    };
  },
};

export default dataTransformers;
